import {Box, Grid, Table, TableBody, TableCell, TableRow, Typography} from '@mui/material';
import React from 'react';
import {useFormContext} from 'react-hook-form';
import {i18n} from 'src/i18n';
import {
  ID23_COLOR_FUNDA_OPTIONS,
  ID23_MAQUINA_OPTIONS,
  ID23_MICROPERFORADO_OPTIONS,
  ID23_PRODUCTO_A_FABRICAR_OPTIONS,
  ID23_SOPORTACONO_OPTIONS,
  toMedicionSelectOptions,
} from 'src/modules/tarea/medicionesParcialId23';
import CheckboxFormItem from 'src/view/shared/form/items/CheckboxFormItem';
import InputNumberFormItem from 'src/view/shared/form/items/InputNumberFormItem';
import SelectFormItem from 'src/view/shared/form/items/SelectFormItem';

const cellSx = {
  border: '1px solid',
  borderColor: 'divider',
  p: 0.5,
  verticalAlign: 'middle',
};

const labelCellSx = {
  ...cellSx,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  width: 72,
};

const titleSx = {fontWeight: 700, fontSize: 13, textTransform: 'uppercase', mb: 0.5};

const compactInputSx = {
  '& .MuiInputBase-root': {fontSize: 13},
  '& .MuiInputBase-input': {py: 0.75, px: 1},
};

function MedicionInput({
  name,
  disabled,
  required,
  notApplicable,
}: {
  name: string;
  disabled?: boolean;
  required?: boolean;
  notApplicable?: boolean;
}) {
  if (notApplicable) {
    return (
      <Typography sx={{fontSize: 13, textAlign: 'center', py: 0.75}}>
        {i18n('common.notApplicable')}
      </Typography>
    );
  }
  return (
    <InputNumberFormItem
      name={name}
      label=""
      required={Boolean(required)}
      disabled={disabled}
      disableFormat
      size="small"
      style={compactInputSx}
    />
  );
}

function MedicionSelect({
  name,
  catalog,
  disabled,
  required,
}: {
  name: string;
  catalog: readonly string[];
  disabled?: boolean;
  required?: boolean;
}) {
  const {watch} = useFormContext();
  const current = watch(name);
  return (
    <SelectFormItem
      name={name}
      label=""
      required={Boolean(required)}
      disabled={disabled}
      options={toMedicionSelectOptions(catalog, current)}
      isClearable={!required}
      selectAllButton={false}
      margin="none"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
    />
  );
}

function NaCheckbox({name, disabled}: {name: string; disabled?: boolean}) {
  return (
    <CheckboxFormItem
      name={name}
      label={i18n('common.notApplicable')}
      disabled={disabled}
      size="small"
      style={{marginTop: -6, marginBottom: -6, marginRight: 0}}
    />
  );
}

function SectionHeader({
  title,
  required,
  naName,
  disabled,
}: {
  title: string;
  required?: boolean;
  naName?: string;
  disabled?: boolean;
}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5, minHeight: 28}}>
      <Typography sx={{...titleSx, mb: 0} as any}>
        {title}
        {required ? (
          <Box component="span" sx={{color: 'error.main'}}>
            {' *'}
          </Box>
        ) : null}
      </Typography>
      {naName ? <NaCheckbox name={naName} disabled={disabled} /> : null}
    </Box>
  );
}

function SectionTable({
  title,
  subtitle,
  required,
  naName,
  disabled,
  children,
}: {
  title: string;
  subtitle?: string;
  required?: boolean;
  naName?: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const {watch} = useFormContext();
  const isNa = naName ? Boolean(watch(naName)) : false;
  return (
    <Box>
      <SectionHeader title={title} required={required} naName={naName} disabled={disabled} />
      {subtitle && (
        <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
          {subtitle}
        </Typography>
      )}
      <Box sx={isNa ? {opacity: 0.55, pointerEvents: 'none'} : undefined}>
        <Table size="small" sx={{borderCollapse: 'collapse'}}>
          <TableBody>{children}</TableBody>
        </Table>
      </Box>
    </Box>
  );
}

function MedicionesParcialId23Section({disabled}: {disabled?: boolean}) {
  const {watch} = useFormContext();
  const baseName = 'medicionesParcial';

  if (!watch('primeroDelParcial')) {
    return null;
  }

  const naTermo = Boolean(watch(`${baseName}.na_termocontraccion`));
  const naCof = Boolean(watch(`${baseName}.na_cof`));
  const naTension = Boolean(watch(`${baseName}.na_tension_bobinado`));
  const naCorriente = Boolean(watch(`${baseName}.na_corriente_estatica`));

  return (
    <Box mb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SectionTable
            title={i18n('entities.tarea.medicionesParcial.termocontraccion')}
            required
            naName={`${baseName}.na_termocontraccion`}
            disabled={disabled}>
            <TableRow>
              <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.termocontraccionT')}</TableCell>
              <TableCell sx={cellSx}>
                <MedicionInput name={`${baseName}.termocontraccion_t`} disabled={disabled} notApplicable={naTermo} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.termocontraccionL')}</TableCell>
              <TableCell sx={cellSx}>
                <MedicionInput name={`${baseName}.termocontraccion_l`} disabled={disabled} notApplicable={naTermo} />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable
            title={i18n('entities.tarea.medicionesParcial.cof')}
            required
            naName={`${baseName}.na_cof`}
            disabled={disabled}>
            <TableRow>
              <TableCell sx={labelCellSx as any} />
              <TableCell sx={{...cellSx, fontWeight: 700, textAlign: 'center'}}>
                {i18n('entities.tarea.medicionesParcial.estatico')}
              </TableCell>
              <TableCell sx={{...cellSx, fontWeight: 700, textAlign: 'center'}}>
                {i18n('entities.tarea.medicionesParcial.dinamico')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.interno')}</TableCell>
              <TableCell sx={cellSx}>
                <MedicionInput name={`${baseName}.cof_interno_estatico`} disabled={disabled} notApplicable={naCof} />
              </TableCell>
              <TableCell sx={cellSx}>
                <MedicionInput name={`${baseName}.cof_interno_dinamico`} disabled={disabled} notApplicable={naCof} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.externo')}</TableCell>
              <TableCell sx={cellSx}>
                <MedicionInput name={`${baseName}.cof_externo_estatico`} disabled={disabled} notApplicable={naCof} />
              </TableCell>
              <TableCell sx={cellSx}>
                <MedicionInput name={`${baseName}.cof_externo_dinamico`} disabled={disabled} notApplicable={naCof} />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable
            title={i18n('entities.tarea.medicionesParcial.tensionBobinado')}
            required
            naName={`${baseName}.na_tension_bobinado`}
            disabled={disabled}>
            <TableRow>
              <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.inicio')}</TableCell>
              <TableCell sx={cellSx}>
                <MedicionInput name={`${baseName}.tension_bobinado_inicio`} disabled={disabled} notApplicable={naTension} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.termino')}</TableCell>
              <TableCell sx={cellSx}>
                <MedicionInput name={`${baseName}.tension_bobinado_termino`} disabled={disabled} notApplicable={naTension} />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable
            title={i18n('entities.tarea.medicionesParcial.corrienteEstatica')}
            required
            naName={`${baseName}.na_corriente_estatica`}
            disabled={disabled}>
            <TableRow>
              <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.inicio')}</TableCell>
              <TableCell sx={cellSx}>
                <MedicionInput name={`${baseName}.corriente_estatica_inicio`} disabled={disabled} notApplicable={naCorriente} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.termino')}</TableCell>
              <TableCell sx={cellSx}>
                <MedicionInput name={`${baseName}.corriente_estatica_termino`} disabled={disabled} notApplicable={naCorriente} />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.productoAFabricar')} required>
            <TableRow>
              <TableCell sx={cellSx}>
                <MedicionSelect
                  name={`${baseName}.producto_a_fabricar`}
                  catalog={ID23_PRODUCTO_A_FABRICAR_OPTIONS}
                  disabled={disabled}
                  required
                />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.maquina')} required>
            <TableRow>
              <TableCell sx={cellSx}>
                <MedicionSelect name={`${baseName}.maquina`} catalog={ID23_MAQUINA_OPTIONS} disabled={disabled} required />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.colorFunda')} required>
            <TableRow>
              <TableCell sx={cellSx}>
                <MedicionSelect name={`${baseName}.color_funda`} catalog={ID23_COLOR_FUNDA_OPTIONS} disabled={disabled} required />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.microperforado')} required>
            <TableRow>
              <TableCell sx={cellSx}>
                <MedicionSelect
                  name={`${baseName}.microperforado`}
                  catalog={ID23_MICROPERFORADO_OPTIONS}
                  disabled={disabled}
                  required
                />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.pesoRollo')} required>
            <TableRow>
              <TableCell sx={cellSx}>
                <MedicionInput name={`${baseName}.peso_rollo`} disabled={disabled} required />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.soportaconos')} required>
            <TableRow>
              <TableCell sx={cellSx}>
                <MedicionSelect name={`${baseName}.soportacono`} catalog={ID23_SOPORTACONO_OPTIONS} disabled={disabled} required />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>
      </Grid>
    </Box>
  );
}

export default MedicionesParcialId23Section;
